import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { productFeatures } from "@/components/new-site/home/NewSiteHomeContent";
import { CalendarCheck, LayoutGrid, MapPinned, ScanSearch, Scale, Users, type LucideIcon } from "lucide-react";
import fostonBeckAsset from "@/assets/new-site/foston-beck-national-highways.jpg.asset.json";
import hardwareVideoAsset from "@/assets/new-site/hardware-video.mp4.asset.json";
import hardwarePosterAsset from "@/assets/new-site/hardware-poster.jpg.asset.json";
import fieldFallback from "@/assets/preview-hero.jpg";
import NewSiteProcessSection, { newSiteProcessSteps } from "@/components/new-site/NewSiteProcessSection";
import { NsButton } from "@/components/new-site/NewSiteChrome";
import MobileHomeCarousel from "@/components/new-site/home/MobileHomeCarousel";
import "./mobile-solution.css";

const integrationAlt = "Diagram showing flood, streetworks, construction and utility monitoring feeds flowing into the Snapshot Live portal, which routes images, structured data, alerts and AI insights through an API to dashboards, asset systems, operational workflows, alerts and reports";
import integrationAsset from "@/assets/new-site/integration-ecosystem-v5.png.asset.json";

const specifications = [
  "Solar powered with year-round autonomy",
  "Optimised for seamless connectivity with multi network cellular connectivity",
  "High-resolution optical sensors for fine detail in any light",
  "IP68 weatherproof enclosure rated for heavy wind, rain, and grit",
];


const platformFeatures = [
  { title: "Periodic Image Schedules", body: "Dictate precise capture intervals matching your project tempo. Build verifiable visual timelines without wasting data or network bandwidth on empty footage." },
  { title: "Instant On-Demand Captures", body: "Trigger real-time high-fidelity images directly from the portal when checking active alerts, verifying site access, or assessing immediate weather events." },
  { title: "Privacy Blurring Protocols", body: "Automated on-device and cloud-based obfuscation masks faces, public highways, and non-target boundaries, preserving full regulatory compliance with UK data standards." },
  { title: "AI Powered Alerts", body: "Our vision algorithms process inbound frames, instantly notifying your team of key changes, anomalies, or unexpected site activity without constant human monitoring." },
];

const visibilityBenefits = [
  { number: "01", title: "Avoided Activity", body: "Fewer low-value journeys, call-outs, or routine checks where remote evidence is sufficient." },
  { number: "02", title: "Early Action", body: "Intervene before obstruction, deterioration, non-compliance, or weather-related impact becomes more expensive." },
  { number: "03", title: "Better Decisions", body: "Visual context reduces uncertainty around alarms, reports, and remote conditions." },
  { number: "04", title: "Strong Assurance", body: "A consistent record can reduce the effort required to demonstrate what happened, when it happened, and what was done." },
];

type VisibilityMetric = { title: string; body?: string; icon: LucideIcon };

const visibilityMetrics: VisibilityMetric[] = [
  { title: "Coverage", body: "Temporary, remote and off grid locations that traditionally remain unmonitored.", icon: MapPinned },
  { title: "Density", body: "More monitored locations without the infrastructure burden of video.", icon: LayoutGrid },
  { title: "Longevity", body: "Longer-term deployment without creating a new maintenance workload.", icon: CalendarCheck },
  { title: "Shared understanding", body: "A current image is quickly understood across operations, engineering, contractors and control rooms.", icon: Users },
  { title: "AI-ready evidence", body: "Consistent images support classification, change detection and prioritisation.", icon: ScanSearch },
  { title: "Proportionate data", body: "Capture enough evidence to support the decision without creating unnecessary data or surveillance.", icon: Scale },
];

/** Figma Our Solution, opening and hardware sections; media awaits final exports. */
export default function NewSiteSolutionContent({ onContact }: { onContact?: () => void }) {
  const [openFeature, setOpenFeature] = useState<number | null>(null);
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const { hash } = useLocation();
  const linkedFeature = productFeatures.findIndex((feature) => hash === `#${feature.solutionId}`);
  useEffect(() => {
    const feature = productFeatures[linkedFeature];
    if (!feature) return;
    // Wait for the phone carousel to select its slide before positioning the page.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const target = document.getElementById(feature.solutionId);
        if (!target) return;
        const navHeight = document.querySelector(".ns-workspace > header")?.getBoundingClientRect().height ?? 72;
        window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - navHeight - 16, behavior: "instant" as ScrollBehavior });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [linkedFeature]);
  // Solution cards map 1:1, in order, to the homepage Product & Features entries.
  const detail = openFeature !== null ? productFeatures[openFeature] : null;
  return (
    <div className="ns-solution">
      <section aria-labelledby="solution-heading" className="ns-solution-hero grid bg-ns-white lg:min-h-[min(701px,calc(100svh-100px))] lg:grid-cols-2">
        <div className="flex items-center px-6 py-14 md:px-[72px] lg:px-[94px] lg:py-24">
          <div className="flex w-full max-w-[547px] flex-col items-start gap-6 text-left">
            <h1 id="solution-heading" className="text-[28px] font-medium leading-[1.15] tracking-normal md:text-[40px]">
              <span className="block mb-6 text-[12px] font-medium leading-[19.1px] tracking-normal text-ns-plum/50">The Solution</span>
              Autonomous visibility. Engineered to go anywhere.
            </h1>
            <p className="max-w-[532px] text-[18px] font-normal leading-[1.5] text-ns-plum/50">
              Snapshot Live bridges the gap between periodic site inspections and traditional CCTV. Rapid to deploy and fully autonomous, it delivers actionable visual intelligence from almost any environment, without the cost and complexity of continuous video monitoring.
            </p>
          </div>
        </div>
        <figure className="relative min-h-[350px] overflow-hidden bg-ns-beige lg:min-h-[min(701px,calc(100svh-100px))]">
          <img src={fostonBeckAsset.url} alt="Snapshot Live solar-powered camera installed on site" onError={(event) => { if (event.currentTarget.src !== new URL(fieldFallback, window.location.href).href) event.currentTarget.src = fieldFallback; }} className="absolute inset-0 h-full w-full object-cover object-left" />
          <div className="absolute inset-x-0 bottom-0 h-[220px] bg-gradient-to-t from-ns-plum/80 to-transparent" />
          <figcaption className="absolute inset-x-0 bottom-7 px-6 text-left text-[15px] font-medium leading-[1.5] text-ns-white lg:px-[34px]">
            Field Proven - Designed to go where others can’t
          </figcaption>
        </figure>
      </section>

      <section aria-labelledby="hardware-heading" className="ns-solution-hardware bg-ns-white px-6 py-14 md:px-[72px] lg:py-[120px]">
        <div className="mx-auto grid max-w-[1296px] items-center gap-8 lg:grid-cols-[minmax(0,589fr)_minmax(0,561fr)] lg:gap-[146px]">
          <div className="relative aspect-[589/506] min-w-0 overflow-hidden bg-ns-white">
            <video autoPlay muted loop playsInline preload="metadata" src={hardwareVideoAsset.url} poster={hardwarePosterAsset.url} aria-label="Snapshot Live camera and solar panel, studio product film" className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden" />
            <img src={hardwarePosterAsset.url} alt="Snapshot Live camera and solar panel" loading="lazy" className="absolute inset-0 hidden h-full w-full object-cover motion-reduce:block" />
          </div>
          <div className="flex min-w-0 flex-col gap-[34px] text-left">
            <div className="flex flex-col items-start gap-6">
              <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">Solar camera hardware</p>
              <h2 id="hardware-heading" className="text-[28px] font-medium leading-[1.15] tracking-normal md:text-[40px]">
                Independent operation, near zero maintenance.
              </h2>
              <p className="text-[18px] font-normal leading-[1.5] text-ns-plum/50">
                Our UK designed and manufactured Snapshot camera utilises high-efficiency solar harvesting coupled with robust cellular technology. Designed specifically for long-term field stability in all weather conditions, with an internal backup battery.
              </p>
            </div>
            <ul className="flex list-disc flex-col gap-3 pl-4 text-[15px] font-medium leading-[1.5]">
              {specifications.map((specification) => <li key={specification}>{specification}</li>)}
            </ul>
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-[1296px] text-left text-[12px] leading-[19.1px] text-ns-plum/50">
          * Engineered to maintain connectivity in low-signal areas; cellular service is required. Autonomy is subject to reasonable usage.
        </p>
      </section>

      <section id="visibility-when-you-need-it" aria-labelledby="platform-heading" className="ns-solution-platform ns-platform-bg flex scroll-mt-20 flex-col gap-16 px-6 py-20 md:px-[72px] md:py-[120px]">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">Visibility when you need it.</p>
          <h2 id="platform-heading" className="text-[28px] font-medium leading-[1.15] tracking-normal md:text-[40px]">On-Demand Intelligence, Total Control</h2>
          <p className="max-w-[688px] text-[18px] font-normal leading-[1.5] text-ns-plum/50">
            Overcoming the visibility gap by providing an efficient image based solution that reduces need for physical inspections and unnecessary video overhead.
          </p>
        </div>
        <MobileHomeCarousel label="platform features" narrow initialIndex={Math.max(linkedFeature, 0)} desktopClassName="grid auto-rows-fr gap-8 md:grid-cols-2">
          {platformFeatures.map((f, index) => (
            <button
              key={f.title}
              id={productFeatures[index]?.solutionId}
              type="button"
              onClick={() => setOpenFeature(index)}
              aria-label={`${f.title}: view details`}
              className="group flex h-full min-h-[156px] w-full scroll-mt-20 flex-col justify-center gap-4 rounded-lg bg-ns-white p-6 text-left transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ns-plum"
            >
              <h3 className="text-[18px] font-medium leading-[1.3]">{f.title}</h3>
              <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{f.body}</p>
              <span className="inline-flex items-center text-ns-plum" aria-hidden="true">
                <Plus className="h-6 w-6" />
              </span>
            </button>
          ))}
        </MobileHomeCarousel>
        <Dialog open={openFeature !== null} onOpenChange={(open) => { if (!open) setOpenFeature(null); }}>
          <DialogContent className="max-w-[760px] gap-5 border-none bg-ns-white p-6 text-left text-ns-plum md:p-8">
            {detail && (
              <>
                <img src={detail.image} alt={detail.imageAlt} className={`h-[220px] w-full rounded-lg md:h-[340px] ${(detail as { contain?: boolean }).contain ? "object-contain" : "object-cover"}`} />
                <DialogTitle className="text-left text-[22px] font-medium leading-[1.3] md:text-[27px]">{detail.title}</DialogTitle>
                <DialogDescription className="text-left text-[15px] font-medium leading-[1.5] text-ns-plum/60">{detail.body}</DialogDescription>
                {(detail as { detailBody?: string }).detailBody && (
                  <p className="text-left text-[15px] font-medium leading-[1.5] text-ns-plum/60">
                    {(detail as { detailBody?: string }).detailBody}
                  </p>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </section>

      <section aria-labelledby="visibility-economics-heading" className="ns-solution-economics ns-process-bg flex flex-col px-[72px] py-[120px] text-ns-white">
        <div className="mx-auto flex w-full max-w-[1296px] flex-col gap-16">
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-[12px] font-medium leading-[19.1px] md:hidden">Process</p>
            <h2 id="visibility-economics-heading" className="text-[40px] font-medium leading-[1.15] tracking-normal">The economics of visibility</h2>
            <p className="max-w-[579px] text-[15px] font-medium leading-[1.5]">
              The business case is bigger than the cost of a camera. The real comparison is the cost of obtaining trustworthy field visibility versus the downstream cost of not having it.
            </p>
          </div>
          <div aria-hidden="true" className="h-px w-full bg-ns-white/30" />
          <div className="grid grid-cols-4 items-start gap-8 text-left">
            {visibilityBenefits.map((benefit) => (
              <div key={benefit.number} className="flex min-w-0 flex-col items-start gap-4">
                <p className="text-[15px] font-medium leading-[1.5]">{benefit.number}</p>
                <h3 className="text-[27px] font-medium leading-[1.3] tracking-normal">{benefit.title}</h3>
                <p className="text-[15px] font-medium leading-[1.5]">{benefit.body}</p>
              </div>
            ))}
          </div>
          <div aria-hidden="true" className="h-px w-full bg-ns-white/30" />
        </div>
      </section>

      <section aria-labelledby="visibility-metrics-heading" className="ns-solution-metrics bg-ns-beige px-6 py-14 text-ns-plum md:px-[72px] md:py-[120px]">
        <div className="mx-auto flex w-full max-w-[1296px] flex-col gap-[39px]">
          <div className="flex flex-col items-center gap-3 text-center">
            <h2 id="visibility-metrics-heading" className="text-[40px] font-medium leading-[1.15] tracking-normal">Why still images change the economics.</h2>
            <p className="max-w-[645px] text-[15px] font-medium leading-[1.5] text-ns-plum/50">
              Use continuous video where continuous motion matters. Use sensors where a variable matters. Use autonomous imagery where the operational question is visual condition.
            </p>
          </div>
          {/* Phones get the same full-width accordion the homepage "Visibility Gap" list uses:
              icon and headline on one line, plus on the right, detail unfolding underneath. */}
          <Accordion type="single" collapsible className="ns-metric-accordion flex flex-col gap-3 md:hidden">
            {visibilityMetrics.map((metric) => (
              <AccordionItem key={metric.title} value={metric.title} className="rounded-lg border-0 bg-ns-white px-4">
                <AccordionTrigger className="ns-metric-trigger gap-3 py-3.5 text-left text-[16px] font-medium leading-[1.3] text-ns-plum hover:no-underline">
                  <span className="flex min-w-0 items-center gap-3">
                    <metric.icon aria-hidden="true" className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span>{metric.title}</span>
                  </span>
                  <span aria-hidden="true" className="ns-metric-plus flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-ns-plum/25 text-ns-plum">
                    <Plus className="h-4 w-4 transition-transform duration-200 motion-reduce:transition-none" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-4 pl-8 pr-1 text-[13px] font-normal leading-[1.5] text-ns-plum/50">{metric.body}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="hidden grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-[39px] md:grid">
            {visibilityMetrics.map((metric) => (
              <article key={metric.title} className="flex min-w-0 flex-col items-start gap-4 rounded-lg bg-ns-white p-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-start" aria-hidden="true">
                  <metric.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="w-full text-left text-[18px] font-medium leading-[1.3] tracking-normal">{metric.title}</h3>
                {metric.body && (
                  <p className="w-full text-left text-[13px] font-normal leading-[1.5] text-ns-plum/50">{metric.body}</p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="integration-heading" className="ns-solution-integration bg-ns-white px-6 py-14 md:px-[72px] lg:py-[120px]">
        <div className="mx-auto grid max-w-[1296px] items-center gap-8 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col items-start gap-5 text-left">
            <p className="text-[12px] font-medium leading-[17px] text-ns-plum">Integration</p>
            <h2 id="integration-heading" className="text-[24px] font-medium leading-[1.3] md:text-[27px]">Secure Portal &amp; API Ecosystem</h2>
            <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">
              Access your data anywhere with multi-tenant client portals, custom stakeholder views, and simple endpoints. Push scheduled stills and/or meta data directly into existing systems, asset logs, and project control rooms.
            </p>
          </div>
          <button
            type="button"
            aria-label="Expand the integration diagram"
            onClick={() => setIntegrationOpen(true)}
            className="group relative block aspect-[1672/941] w-full cursor-zoom-in overflow-hidden rounded-lg bg-ns-white ring-1 ring-ns-plum/10"
          >
            <img
              src={integrationAsset.url}
              alt={integrationAlt}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </button>
        </div>
      </section>

      <Dialog open={integrationOpen} onOpenChange={setIntegrationOpen}>
        <DialogContent className="w-[min(96vw,1400px)] max-w-none rounded-[8px] bg-ns-white p-2 font-archivo text-ns-plum sm:p-3">
          <DialogTitle className="sr-only">Integration diagram</DialogTitle>
          <DialogDescription className="sr-only">
            Diagram showing monitoring feeds flowing into the Snapshot Live portal and out through an API to dashboards, asset systems, workflows, alerts and reports.
          </DialogDescription>
          <img
            src={integrationAsset.url}
            alt={integrationAlt}
            className="h-auto w-full object-contain"
          />
        </DialogContent>
      </Dialog>

      <NewSiteProcessSection
        tone="plum"
        title="Easy to use"
        intro="Snapshot Live is designed to deploy quickly and scale across remote sites without complex infrastructure."
        steps={newSiteProcessSteps}
      />

      <section aria-labelledby="solution-cta-heading" className="ns-solution-closing ns-cta-bg flex flex-col items-center gap-8 px-6 py-20 text-center md:px-[72px] md:py-[120px]">
        <div className="flex max-w-[720px] flex-col items-center gap-4">
          <h2 id="solution-cta-heading" className="text-[28px] font-medium leading-[1.15] md:text-[40px]">Ready to try the complete solution?</h2>
          <p className="text-[15px] font-medium leading-[1.5]">Speak with our team to discover how autonomous, image-based monitoring can improve visibility, reduce costs and help you make smarter, faster decisions.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <NsButton variant="lime" onClick={onContact}>Contact Us</NsButton>
          <NsButton onClick={onContact}>Request Case Studies</NsButton>
        </div>
      </section>
    </div>
  );
}